import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../components/wrapper/API';
import { sortRecipientsByName } from '../util/flatRates';
import { normalizeRole } from '../util/participantGroups';

/**
 * Persons that can receive a flat rate: current role holders (Orga/Admin), from
 * GET /FlatRate/RecipientCandidates → [{ playerId, name, role }]. `ensureLoaded()` fetches at most once per
 * component instance (call it when the flat-rate editor opens). `byId` maps playerId → candidate, for
 * labels of existing rows; a row whose person lost the role is not in the list (show its server name).
 */
export default function useFlatRateRecipients() {
  const [candidates, setCandidates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const requestedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const ensureLoaded = useCallback(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    setLoading(true);
    setFailed(false);
    api.get('/FlatRate/RecipientCandidates')
      .then(res => {
        if (!mountedRef.current) return;
        const list = (Array.isArray(res.data) ? res.data : [])
          .filter(c => c && c.playerId != null)
          .map(c => ({ playerId: Number(c.playerId), name: String(c.name ?? ''), role: normalizeRole(c.role) }));
        setCandidates(sortRecipientsByName(list));
        setLoaded(true);
      })
      .catch(() => {
        requestedRef.current = false;
        if (mountedRef.current) setFailed(true);
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, []);

  const byId = useMemo(() => new Map(candidates.map(c => [c.playerId, c])), [candidates]);

  return { candidates, byId, loaded, loading, failed, ensureLoaded };
}
